import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { runDocumentChat } from '@/lib/documentAi';
import { parseAiPayload, type AiChartSpec, type AiTableSpec } from '@/lib/aiSheet';
import {
  deleteChatSession,
  listChatSessions,
  makeSessionId,
  relativeSessionDate,
  saveChatSession,
  sessionTitle,
  type EditorChatSession,
} from '@/lib/editorChatHistory';

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

interface EditorAiChatPanelProps {
  open: boolean;
  mode: 'excel' | 'word';
  onClose: () => void;
  /** Contenu actuel du document, envoyé comme contexte à l'IA. */
  getContext: () => string;
  /** Éditeur de texte : insérer du HTML au curseur. */
  onInsertText?: (html: string) => void;
  /** Éditeur de texte : remplacer tout le document. */
  onReplaceText?: (html: string) => void;
  /** Tableur : écrire un tableau à partir de la cellule active. */
  onApplyTable?: (table: AiTableSpec) => void;
  /** Tableur : créer un graphique dans la feuille. */
  onApplyChart?: (chart: AiChartSpec) => void;
  /** Clé de stockage de l'historique (en général l'id du document). */
  historyKey?: string;
}

const EXCEL_SUGGESTIONS = [
  'Crée un rapport trimestriel d’activité avec chiffres et graphique',
  'Liste les hommes les plus riches avec leur fortune, classe-les et génère un graphique par secteur',
  'Crée un tableau de suivi de budget mensuel avec formules',
  'Analyse mes données et propose des indicateurs clés',
];

const WORD_SUGGESTIONS = [
  'Crée un plan d’argumentaire commercial',
  'Dresse une liste d’idées pour un article de blog',
  'Résume le document en 5 points clés',
  'Rédige une lettre de motivation',
];

function textToHtml(text: string): string {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  if (/<(p|h[1-6]|ul|ol|li|div|table|blockquote)\b/i.test(trimmed)) return trimmed;
  return trimmed
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

function ChartPreview({ chart }: { chart: AiChartSpec }) {
  const values = chart.series[0]?.values ?? [];
  const max = Math.max(...values, 1);
  if (chart.kind === 'pie') {
    const total = values.reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    const palette = ['#0f766e', '#ca8a04', '#b45309', '#7c2d12', '#4d7c0f', '#9f1239', '#1e3a8a'];
    const stops = values
      .map((v, i) => {
        const start = (acc / total) * 100;
        acc += v;
        const end = (acc / total) * 100;
        return `${palette[i % palette.length]} ${start}% ${end}%`;
      })
      .join(', ');
    return (
      <div className="flex items-center gap-3">
        <div
          className="w-20 h-20 rounded-full flex-shrink-0"
          style={{ background: `conic-gradient(${stops})` }}
        ></div>
        <div className="flex-1 min-w-0 space-y-1">
          {chart.categories.slice(0, 6).map((c, i) => (
            <div key={c} className="flex items-center gap-1.5 text-[11px] text-foreground-600 min-w-0">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: palette[i % palette.length] }}
              ></span>
              <span className="truncate">{c}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-1.5 h-24">
      {values.slice(0, 12).map((v, i) => (
        <div key={`${chart.categories[i] ?? i}`} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
          <div
            className={`w-full rounded-t ${chart.kind === 'line' ? 'bg-accent-400/70' : 'bg-accent-500/80'}`}
            style={{ height: `${Math.max(4, (v / max) * 100)}%` }}
            title={`${chart.categories[i] ?? i} : ${v}`}
          ></div>
          <span className="mt-1 w-full text-[9px] text-foreground-400 truncate text-center">
            {chart.categories[i] ?? i}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Panneau de chat IA latéral, partagé par le tableur et le traitement de texte.
 * - poignée de redimensionnement sur le bord gauche
 * - bouton de réduction (mode rail) et de fermeture
 */
export default function EditorAiChatPanel({
  open,
  mode,
  onClose,
  getContext,
  onInsertText,
  onReplaceText,
  onApplyTable,
  onApplyChart,
  historyKey = '',
}: EditorAiChatPanelProps) {
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(380);
  const [minimized, setMinimized] = useState(false);
  const [appliedIndex, setAppliedIndex] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState(() => makeSessionId());
  const [sessionStart, setSessionStart] = useState(() => Date.now());
  const [sessions, setSessions] = useState<EditorChatSession[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);

  // Historique local des conversations (par document).
  useEffect(() => {
    if (!historyKey) { setSessions([]); return; }
    setSessions(listChatSessions(historyKey));
  }, [historyKey]);

  useEffect(() => {
    if (!historyKey || messages.length === 0) return;
    const next = saveChatSession(historyKey, {
      id: sessionId,
      title: sessionTitle(messages),
      mode,
      createdAt: sessionStart,
      updatedAt: Date.now(),
      messages,
    });
    setSessions(next);
  }, [messages, historyKey, sessionId, mode, sessionStart]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const next = Math.min(760, Math.max(300, window.innerWidth - e.clientX));
      setWidth(next);
    };
    const onUp = () => {
      resizingRef.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  if (!open) return null;

  if (minimized) {
    return (
      <div className="flex flex-col items-center gap-2 w-14 flex-shrink-0 border-l border-background-200/70 bg-background-50 py-3 max-lg:absolute max-lg:inset-y-0 max-lg:right-0 max-lg:z-40">
        <button
          onClick={() => setMinimized(false)}
          className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center cursor-pointer hover:bg-accent-200/70 transition-colors"
          title="Agrandir le chat IA"
        >
          <i className="ri-sparkling-line"></i>
        </button>
        <span
          className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400"
          style={{ writingMode: 'vertical-rl' }}
        >
          Assistant IA
        </span>
        <button
          onClick={onClose}
          className="mt-auto w-9 h-9 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer"
          title="Fermer"
        >
          <i className="ri-close-line"></i>
        </button>
      </div>
    );
  }

  const send = async (prompt?: string) => {
    const question = (prompt ?? input).trim();
    if (!question || loading) return;
    setInput('');
    setError(null);
    const nextMessages: ChatTurn[] = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setLoading(true);
    try {
      const result = await runDocumentChat({
        mode,
        message: question,
        context: getContext(),
        history: messages.slice(-6).map((m) => ({ role: m.role, content: m.content })),
      });
      setMessages([...nextMessages, { role: 'assistant', content: result.result }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const startResize = (e: ReactMouseEvent) => {
    e.preventDefault();
    resizingRef.current = true;
  };

  const resetConversation = () => {
    setMessages([]);
    setError(null);
    setAppliedIndex(null);
    setSessionId(makeSessionId());
    setSessionStart(Date.now());
    setShowHistory(false);
  };

  const loadSession = (session: EditorChatSession) => {
    setMessages(session.messages.map((m) => ({ role: m.role, content: m.content })));
    setSessionId(session.id);
    setSessionStart(session.createdAt);
    setAppliedIndex(null);
    setShowHistory(false);
  };

  const removeSession = (id: string) => {
    const next = deleteChatSession(historyKey, id);
    setSessions(next);
    if (id === sessionId) resetConversation();
  };

  const suggestions = mode === 'excel' ? EXCEL_SUGGESTIONS : WORD_SUGGESTIONS;

  return (
    <div
      className="relative flex flex-col flex-shrink-0 border-l border-background-200/70 bg-background-50 max-lg:absolute max-lg:inset-y-0 max-lg:right-0 max-lg:z-40 max-lg:w-full max-lg:max-w-full"
      style={{ width: typeof window !== 'undefined' && window.innerWidth < 1024 ? '100%' : width }}
    >
      {/* poignée de redimensionnement */}
      <div
        onMouseDown={startResize}
        className="absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 cursor-col-resize hover:bg-accent-300/60 transition-colors"
        title="Glisser pour redimensionner"
      ></div>

      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-background-200/70">
        <span className="w-7 h-7 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-sparkling-line text-sm"></i>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-950 truncate">Assistant IA</p>
          <p className="text-[10px] text-foreground-400 truncate">
            {mode === 'excel' ? 'Il écrit tableaux et graphiques dans la feuille' : 'Il écrit directement dans le document'}
          </p>
        </div>
        <button
          onClick={() => setShowHistory((v) => !v)}
          className={`relative w-7 h-7 rounded-full flex items-center justify-center cursor-pointer ${
            showHistory ? 'bg-background-200 text-foreground-800' : 'text-foreground-400 hover:bg-background-100'
          }`}
          title="Historique des conversations"
        >
          <i className="ri-history-line text-sm"></i>
          {sessions.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-accent-500 text-background-50 text-[9px] font-bold flex items-center justify-center">
              {sessions.length > 9 ? '9+' : sessions.length}
            </span>
          )}
        </button>
        <button
          onClick={resetConversation}
          className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer"
          title="Nouvelle conversation"
        >
          <i className="ri-add-line text-sm"></i>
        </button>
        <button
          onClick={() => setMinimized(true)}
          className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer"
          title="Réduire"
        >
          <i className="ri-subtract-line text-sm"></i>
        </button>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer"
          title="Fermer"
        >
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {showHistory && (
        <div className="border-b border-background-200/70 bg-white max-h-64 overflow-y-auto">
          <div className="flex items-center justify-between px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
              Conversations précédentes
            </p>
            <button
              onClick={resetConversation}
              className="text-[10px] font-semibold text-primary-600 hover:text-primary-800 cursor-pointer whitespace-nowrap"
            >
              + Nouvelle
            </button>
          </div>
          {sessions.length === 0 ? (
            <p className="px-3 pb-3 text-[11px] text-foreground-400">
              Aucune conversation enregistrée pour ce document.
            </p>
          ) : (
            <ul className="pb-2">
              {sessions.map((s) => (
                <li key={s.id} className="group flex items-center gap-1.5 px-2">
                  <button
                    onClick={() => loadSession(s)}
                    className={`flex-1 min-w-0 flex flex-col gap-0.5 px-2 py-1.5 rounded-lg text-left cursor-pointer transition-colors ${
                      s.id === sessionId ? 'bg-primary-50' : 'hover:bg-background-100'
                    }`}
                  >
                    <span className="text-[11px] font-medium text-foreground-800 truncate">{s.title}</span>
                    <span className="text-[9px] text-foreground-400">
                      {relativeSessionDate(s.updatedAt)} · {s.messages.length} message{s.messages.length > 1 ? 's' : ''}
                    </span>
                  </button>
                  <button
                    onClick={() => removeSession(s.id)}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-foreground-300 hover:text-red-500 hover:bg-red-50 cursor-pointer flex-shrink-0"
                    title="Supprimer cette conversation"
                  >
                    <i className="ri-delete-bin-line text-[10px]"></i>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-base font-semibold text-foreground-800 leading-snug">
              {mode === 'excel' ? 'Que dois-je construire dans votre feuille ?' : 'Que dois-je rédiger dans votre document ?'}
            </p>
            <div className="space-y-1.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => void send(s)}
                  className="w-full flex items-start gap-2 px-2.5 py-2 rounded-lg text-left text-xs text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
                >
                  <i className="ri-corner-down-right-line text-foreground-400 mt-0.5 flex-shrink-0"></i>
                  <span>{s}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, index) => {
          if (m.role === 'user') {
            return (
              <div key={index} className="flex justify-end">
                <div className="max-w-[85%] px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            );
          }
          const payload = parseAiPayload(m.content);
          return (
            <div key={index} className="space-y-2">
              {payload.text && (
                <div className="px-3 py-2 rounded-lg bg-white border border-background-200/70 text-xs text-foreground-800 whitespace-pre-wrap">
                  {payload.text}
                </div>
              )}
              {payload.table && (
                <div className="rounded-lg border border-background-200/70 bg-white overflow-hidden">
                  <div className="max-h-56 overflow-auto">
                    <table className="w-full text-[11px] border-collapse">
                      <thead>
                        <tr className="bg-background-100/80">
                          {payload.table.headers.map((h, i) => (
                            <th key={i} className="px-2 py-1.5 text-left font-semibold text-foreground-700 border-b border-background-200/70 whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {payload.table.rows.slice(0, 40).map((row, ri) => (
                          <tr key={ri}>
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-2 py-1 text-foreground-700 border-b border-background-100/80">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {mode === 'excel' && onApplyTable && (
                    <button
                      onClick={() => {
                        onApplyTable(payload.table!);
                        setAppliedIndex(index);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-medium bg-accent-500 text-background-50 hover:bg-accent-600 cursor-pointer transition-colors"
                    >
                      <i className={appliedIndex === index ? 'ri-check-line' : 'ri-table-line'}></i>
                      {appliedIndex === index ? 'Tableau inséré dans la feuille' : 'Insérer le tableau dans la feuille'}
                    </button>
                  )}
                  {mode === 'word' && onInsertText && (
                    <button
                      onClick={() => {
                        const rows = payload.table!.rows
                          .map((r) => `<tr>${r.map((c) => `<td style="border:1px solid #cbd5e1;padding:6px 8px;">${c}</td>`).join('')}</tr>`)
                          .join('');
                        const head = `<tr>${payload.table!.headers
                          .map((c) => `<th style="border:1px solid #cbd5e1;padding:6px 8px;background:#f1f5f9;text-align:left;">${c}</th>`)
                          .join('')}</tr>`;
                        onInsertText(
                          `<table style="border-collapse:collapse;width:100%;margin:12px 0;"><thead>${head}</thead><tbody>${rows}</tbody></table><p><br></p>`,
                        );
                        setAppliedIndex(index);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-medium bg-accent-500 text-background-50 hover:bg-accent-600 cursor-pointer transition-colors"
                    >
                      <i className={appliedIndex === index ? 'ri-check-line' : 'ri-table-line'}></i>
                      {appliedIndex === index ? 'Tableau inséré' : 'Insérer le tableau'}
                    </button>
                  )}
                </div>
              )}
              {payload.chart && (
                <div className="rounded-lg border border-background-200/70 bg-white p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold text-foreground-800 truncate">{payload.chart.title}</p>
                    <span className="text-[10px] text-foreground-400 uppercase">{payload.chart.kind}</span>
                  </div>
                  <ChartPreview chart={payload.chart} />
                  {mode === 'excel' && onApplyChart && (
                    <button
                      onClick={() => {
                        onApplyChart(payload.chart!);
                        setAppliedIndex(index);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-medium border border-background-200/70 bg-background-50 text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
                    >
                      <i className="ri-bar-chart-box-line"></i>
                      Afficher le graphique dans la feuille
                    </button>
                  )}
                </div>
              )}
              {!payload.table && !payload.chart && payload.text && mode === 'word' && onInsertText && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      onInsertText(textToHtml(payload.text));
                      setAppliedIndex(index);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-[11px] font-medium hover:bg-accent-600 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-insert-row-bottom"></i>
                    Insérer
                  </button>
                  {onReplaceText && (
                    <button
                      onClick={() => {
                        onReplaceText(textToHtml(payload.text));
                        setAppliedIndex(index);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-200/70 bg-white text-foreground-700 text-[11px] font-medium hover:bg-background-100 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line"></i>
                      Remplacer
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <i className="ri-loader-4-line animate-spin"></i>
            L’assistant rédige…
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-[11px]">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span className="flex-1">{error}</span>
          </div>
        )}
      </div>

      <div className="border-t border-background-200/70 p-2.5">
        <div className="rounded-xl border border-background-200/70 bg-white px-2.5 py-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            rows={2}
            maxLength={2000}
            placeholder={mode === 'excel' ? 'Demandez à l’IA…' : 'Demandez à l’IA…'}
            className="w-full resize-none bg-transparent text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-foreground-300">Entrée pour envoyer</span>
            <button
              onClick={() => void send()}
              disabled={!input.trim() || loading}
              className="w-7 h-7 rounded-full bg-accent-500 text-background-50 flex items-center justify-center disabled:opacity-40 cursor-pointer hover:bg-accent-600 transition-colors"
              title="Envoyer"
            >
              <i className="ri-arrow-up-line text-sm"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}